import {
  convertHMSFormatInputToHours,
  snapHoursToWholeSeconds,
  today,
  customFormatDate,
} from "@/Utilites/timerHelper";

/** Confirm starting a timer on a log date that is not the calendar today. */
export function confirmTimerStartOnNonToday(logDate) {
  const todayStr = today();
  if (!logDate || logDate === todayStr) {
    return true;
  }
  return window.confirm(
    `This entry is for ${logDate}, not today (${todayStr}). ` +
      "Start the timer on that date anyway? Any timer currently running will be stopped."
  );
}

/**
 * Read stored hours from a timer log row (decimal or "H:MM" / "H:MM:SS" string).
 */
export function resolveLogBaseHours(row) {
  if (!row) return 0;
  const fields = [row.hours, row.hours_with_timer, row.working_hours];
  for (const raw of fields) {
    if (raw == null || raw === "") continue;
    if (typeof raw === "string" && raw.includes(":")) {
      const parsed = convertHMSFormatInputToHours(raw);
      if (parsed != null && !Number.isNaN(Number(parsed))) {
        return snapHoursToWholeSeconds(parsed);
      }
      const hms = HHMMSStoHoursSafe(raw);
      if (hms != null) return snapHoursToWholeSeconds(hms);
      continue;
    }
    const n = Number(raw);
    if (!Number.isNaN(n)) return snapHoursToWholeSeconds(n);
  }
  return 0;
}

/** Normalize hours fields after read or write (second-aligned for pause/resume). */
export function normalizeTimerLogHours(row) {
  if (!row) return row;
  const h = resolveLogBaseHours(row);
  return {...row, hours: h, hours_with_timer: h, working_hours: h};
}

function HHMMSStoHoursSafe(value) {
  const parts = String(value).trim().split(":");
  if (parts.length === 3) {
    return Number(parts[0]) + Number(parts[1]) / 60 + Number(parts[2]) / 3600;
  }
  if (parts.length === 2) {
    return Number(parts[0]) + Number(parts[1]) / 60;
  }
  return null;
}

/** Log date for a v1 session — legacy row date wins over started_at (cross-day backdating). */
export function resolveSessionLogDate(v1Session, timerLogsList) {
  if (!v1Session) return null;
  if (v1Session.timer_log_date) {
    return v1Session.timer_log_date;
  }
  const legacyId = v1Session.legacy_timer_log_id;
  if (legacyId != null) {
    if (Array.isArray(timerLogsList)) {
      const row = timerLogsList.find((r) => String(r.id) === String(legacyId));
      if (row && (row.timer_log_date || row.log_date)) {
        return row.timer_log_date || row.log_date;
      }
    }
    if (v1Session.timer_log_date) {
      return v1Session.timer_log_date;
    }
    return null;
  }
  if (v1Session.started_at) {
    const fromStarted = customFormatDate(v1Session.started_at, "YYYY-MM-DD");
    return fromStarted;
  }
  return today();
}

/** True when v1 running state may be merged into the timer table for this calendar day. */
export function shouldShowV1SessionOnDate(v1Session, timerLogsList, viewDate) {
  if (!v1Session || v1Session.status !== "active" || !viewDate) {
    return false;
  }
  const sessionDate = resolveSessionLogDate(v1Session, timerLogsList);
  if (!sessionDate || sessionDate !== viewDate) {
    return false;
  }
  const legacyId = v1Session.legacy_timer_log_id;
  if (legacyId != null && Array.isArray(timerLogsList)) {
    const row = timerLogsList.find((r) => String(r.id) === String(legacyId));
    if (!row) {
      return v1Session.timer_log_date === viewDate;
    }
    const rowDate = row.timer_log_date || row.log_date;
    if (rowDate && sessionDate && rowDate !== sessionDate) {
      return false;
    }
    if (rowDate && rowDate !== viewDate) {
      return false;
    }
  }
  return true;
}

/**
 * Map v1 session JSON to legacy timer log shape used by navbar / table components.
 */
export function mapV1SessionToLegacyActive(session, userProfile, legacyRow) {
  if (!session) return {};
  const userId =
    session.user_id != null
      ? session.user_id
      : userProfile && userProfile.user_id;
  const logId =
    session.legacy_timer_log_id != null
      ? session.legacy_timer_log_id
      : session.id;
  const hours =
    session.elapsed_hours != null ? Number(session.elapsed_hours) : 0;
  const logDate =
    (legacyRow && (legacyRow.timer_log_date || legacyRow.log_date)) ||
    session.timer_log_date ||
    (session.started_at
      ? customFormatDate(session.started_at, "YYYY-MM-DD")
      : today());
  return {
    id: logId,
    v1_session_id: session.id,
    user_id: userId,
    project_id: session.project_id ?? null,
    task_description: session.task_description || "",
    log_description: session.task_description || "",
    timer_started_at: session.started_at,
    timer_stopped_at: null,
    timer_initiated_at: session.started_at,
    timer_log_date: logDate,
    log_date: logDate,
    hours,
    hours_with_timer: hours,
    working_hours: hours,
    is_submitted: true,
    _timer_v1: true,
  };
}

export function mergeV1ActiveIntoTimerList(
  timerLogs,
  v1Session,
  userProfile,
  v1BaseHours,
  viewDate
) {
  const list = Array.isArray(timerLogs) ? timerLogs.map((r) => ({...r})) : [];
  if (!v1Session || v1Session.status !== "active") return list;
  const mapped = mapV1SessionToLegacyActive(v1Session, userProfile);
  const activeId =
    v1Session.legacy_timer_log_id != null
      ? v1Session.legacy_timer_log_id
      : mapped.id;
  const storedBase =
    v1BaseHours != null && !Number.isNaN(Number(v1BaseHours))
      ? Number(v1BaseHours)
      : 0;
  const sessionDate = resolveSessionLogDate(v1Session, timerLogs);
  const out = list.map((row) => {
    if (String(row.id) === String(activeId)) {
      const rowDate = row.timer_log_date || row.log_date;
      if (sessionDate && rowDate && rowDate !== sessionDate) {
        return row;
      }
      const rowBase = resolveLogBaseHours(row);
      const baseHours = storedBase > 0 ? storedBase : rowBase;
      const mappedForRow = mapV1SessionToLegacyActive(
        v1Session,
        userProfile,
        row
      );
      return {
        ...row,
        ...mappedForRow,
        id: row.id,
        hours: baseHours,
        hours_with_timer: baseHours,
        working_hours: baseHours,
        timer_started_at: mappedForRow.timer_started_at,
        timer_stopped_at: null,
      };
    }
    if (row.timer_started_at != null) {
      return {...row, timer_started_at: null};
    }
    return row;
  });
  const found = out.some((r) => String(r.id) === String(activeId));
  if (!found && sessionDate && viewDate && sessionDate === viewDate) {
    const mappedForRow = mapV1SessionToLegacyActive(
      v1Session,
      userProfile,
      null
    );
    return [
      {
        ...mappedForRow,
        id: activeId,
        hours: storedBase,
        hours_with_timer: storedBase,
        working_hours: storedBase,
        timer_log_date: sessionDate,
        log_date: sessionDate,
        timer_started_at: mappedForRow.timer_started_at,
        timer_stopped_at: null,
      },
      ...out,
    ];
  }
  return out;
}

/** True when this log row is the single active timer (v1 or legacy). */
export function isActiveTimerRow(row, activeTimer, v1Session) {
  if (!row || row.id == null) return false;
  const rid = String(row.id);
  if (activeTimer && activeTimer.id != null && rid === String(activeTimer.id)) {
    return true;
  }
  if (v1Session && v1Session.status === "active") {
    if (
      v1Session.legacy_timer_log_id != null &&
      rid === String(v1Session.legacy_timer_log_id)
    ) {
      return true;
    }
    if (
      activeTimer &&
      activeTimer.v1_session_id &&
      String(activeTimer.v1_session_id) === String(v1Session.id)
    ) {
      return rid === String(activeTimer.id);
    }
  }
  return false;
}

/** True when the user clicked stop on a row that is running under timer v1. */
export function shouldStopV1ForRow(row, v1Session, activeTimer) {
  if (!row) return false;
  if (isActiveTimerRow(row, activeTimer, v1Session)) return true;
  if (
    v1Session &&
    v1Session.status === "active" &&
    v1Session.legacy_timer_log_id != null &&
    String(row.id) === String(v1Session.legacy_timer_log_id)
  ) {
    return true;
  }
  return false;
}
